import { ChangeEvent, RefCallback, useEffect, useState } from 'react'
import { Input, InputGroup, InputProps, InputRightElement, InputLeftElement, chakra } from '~/design_system'
import useUuid from '~/hooks/useUuid'
import { Close, Search as SearchIcon } from '~/icons'
import { autoFocusRef } from '~/common/domUtils'
import useDebouncedEffect from '~/hooks/useDebouncedEffect'
import { ClientOnly } from '~/common/nextJsUtils'
import { useMsg } from '~/common/localizationUtils'

export type SearchInputProps = Omit<InputProps, 'children' | 'onChange'> & {
  value?: string
  onChange?: (value: string) => void
  autoFocus?: InputProps['autoFocus']
  isDisabled?: boolean
  'data-testid'?: string
}

export const Search = ({
  value = '',
  onChange,
  placeholder,
  autoFocus,
  isDisabled,
  isReadOnly,
  'data-testid': dataTestid,
  ...props
}: SearchInputProps) => {
  const [searchTerm, setSearchTerm] = useState(value)
  useDebouncedEffect(
    () => {
      if (typeof onChange === 'function') onChange(searchTerm)
    },
    { delay: process.env.NODE_ENV === 'test' ? 0 : 250 },
    [searchTerm]
  )

  useEffect(() => {
    setSearchTerm(value)
  }, [value])

  const ref = autoFocus ? (autoFocusRef() as RefCallback<HTMLInputElement>) : null
  const msg = useMsg()

  // eslint-disable-next-line no-param-reassign
  placeholder = placeholder ?? msg('common.ui.search')

  const id = `search-` + useUuid()

  return (
    <InputGroup {...props}>
      <InputLeftElement height="100%">
        <SearchIcon />
      </InputLeftElement>
      <ClientOnly>
        <chakra.label display="none" htmlFor={id}>
          {placeholder}
        </chakra.label>
        <Input
          id={id}
          ref={ref}
          data-testid={dataTestid}
          aria-label={placeholder}
          placeholder={placeholder}
          value={searchTerm}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
          textStyle="bodySmall"
          isDisabled={isDisabled}
          isReadOnly={isReadOnly}
          pr={searchTerm ? 8 : 1}
          pl={9}
        />
      </ClientOnly>
      {searchTerm !== '' && (
        <InputRightElement height="100%">
          <Close color="var(--vndly-color-container-subdued)" onClick={() => setSearchTerm('')} />
        </InputRightElement>
      )}
    </InputGroup>
  )
}
